"use client";

import { useRef, useState, useEffect, useCallback } from "react";
import Link from "next/link";

export default function BlogCarousel({ posts = [] }) {
  const trackRef = useRef(null);
  const [current, setCurrent] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const total = posts.length;

  const scrollTo = useCallback((index) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.children[index];
    if (!card) return;
    track.scrollTo({ left: card.offsetLeft - track.offsetLeft, behavior: "smooth" });
    setCurrent(index);
  }, []);

  const prev = () => scrollTo(Math.max(0, current - 1));
  const next = () => scrollTo(Math.min(total - 1, current + 1));

  // Sync dot indicator and prev/next availability on manual/programmatic scroll.
  // Availability is derived from the actual scrollable range (not the post-index
  // math above) because once fewer cards remain than fit in one view, the browser
  // clamps scrollLeft before it reaches the last card's offset — relying on the
  // index alone left "Next" re-enabling itself after every click near the end.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const updateEdges = () => {
      const maxScroll = track.scrollWidth - track.clientWidth;
      const start = track.scrollLeft <= 1;
      const end = maxScroll <= 1 || track.scrollLeft >= maxScroll - 1;
      setAtStart(start);
      setAtEnd(end);
      return { start, end };
    };

    // When more cards are visible per view than remain past a given point,
    // the browser reaches its max scrollLeft well before scrollLeft/cardWidth
    // rounds up to the last post's index — leaving the dot indicator stuck a
    // few dots short of the end even though there's nowhere left to scroll.
    // Snap to the first/last dot whenever the track is actually at a boundary.
    const onScroll = () => {
      const { start, end } = updateEdges();
      if (start) {
        setCurrent(0);
        return;
      }
      if (end) {
        setCurrent(total - 1);
        return;
      }
      const gap = parseFloat(getComputedStyle(track).columnGap || "0") || 0;
      const cardWidth = (track.children[0]?.offsetWidth ?? 1) + gap;
      const idx = Math.round(track.scrollLeft / cardWidth);
      setCurrent(Math.min(Math.max(idx, 0), total - 1));
    };

    updateEdges();
    track.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", updateEdges);
    return () => {
      track.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", updateEdges);
    };
  }, [total]);

  return (
    <div className="relative">
      {/* Scroll track */}
      <div
        ref={trackRef}
        className="flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 hide-scrollbar"
        style={{ scrollbarWidth: "none" }}
      >
        {posts.map((post) => (
          <Link
            key={post.slug}
            href={`/blog/${post.slug}/`}
            className="group flex-shrink-0 w-[85%] sm:w-[45%] lg:w-[30%] flex flex-col bg-white rounded-2xl overflow-hidden border border-slate-200 hover:border-emerald-200 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1 snap-start"
          >
            <div className="aspect-[3/2] overflow-hidden bg-slate-100">
              <img
                src={post.image}
                alt={post.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="p-6 flex flex-col flex-1">
              {post.date && (
                <p className="text-xs text-slate-600 mb-2">
                  {String(post.date).split(" ")[0]}
                </p>
              )}
              <h3 className="font-semibold text-slate-800 group-hover:text-emerald-700 transition-colors mb-3 leading-snug flex-1">
                {post.title}
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed line-clamp-3">{post.excerpt}</p>
              <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 group-hover:text-emerald-600 transition-colors">
                Read More
                <svg className="w-4 h-4 translate-x-0 group-hover:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
            </div>
          </Link>
        ))}
      </div>

      {/* Prev / Next buttons */}
      <button
        onClick={prev}
        disabled={atStart}
        aria-label="Previous post"
        className="absolute -left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center text-slate-600 hover:text-emerald-700 hover:border-emerald-200 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 z-10"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <button
        onClick={next}
        disabled={atEnd}
        aria-label="Next post"
        className="absolute -right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center text-slate-600 hover:text-emerald-700 hover:border-emerald-200 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 z-10"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Dot indicators */}
      <div className="flex justify-center gap-2 mt-6">
        {posts.map((_, i) => (
          <button
            key={i}
            onClick={() => scrollTo(i)}
            aria-label={`Go to post ${i + 1}`}
            className="p-3 flex items-center justify-center"
          >
            <span
              className={`block rounded-full transition-all duration-300 ${
                i === current
                  ? "w-6 h-2 bg-emerald-600"
                  : "w-2 h-2 bg-slate-300 hover:bg-slate-400"
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
}
